import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  Download,
  FileText,
  Filter,
  Loader2,
  Search,
  Sparkles,
  Tag as TagIcon,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import DocIndexBadge from '../components/cases/DocIndexBadge'
import AiResultModal from '../components/ui/AiResultModal'
import {
  DocumentCategoryLabel,
  type AiSummaryResponse,
  type ClientListResponse,
  type DocumentListItem,
} from '@toga/shared'

const PAGE_SIZE = 50

interface ExtendedDoc extends DocumentListItem {
  caseCode?: string | null
  caseTitle?: string | null
  clientDisplayName?: string | null
}

interface ListResponse {
  items: ExtendedDoc[]
  total: number
  page: number
  pageSize: number
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function Documents() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<ListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState(searchParams.get('q') ?? '')
  const [clients, setClients] = useState<Array<{ id: string; displayName: string }>>([])
  const [summarizingId, setSummarizingId] = useState<string | null>(null)
  const [aiResult, setAiResult] = useState<{
    title: string
    text: string
    conversationId: string | null
  } | null>(null)

  const category = searchParams.get('category') ?? ''
  const tag = searchParams.get('tag') ?? ''
  const clientId = searchParams.get('clientId') ?? ''
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1)

  // Clientes para el filtro (una sola carga).
  useEffect(() => {
    api
      .get<ClientListResponse>('/clients?pageSize=200')
      .then((r) => setClients(r.items.map((c) => ({ id: c.id, displayName: c.displayName }))))
      .catch(() => setClients([]))
  }, [])

  // Debounce búsqueda
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      if (q.trim()) next.set('q', q.trim())
      else next.delete('q')
      next.delete('page')
      setSearchParams(next, { replace: true })
    }, 300)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      const qParam = searchParams.get('q')
      if (qParam) params.set('q', qParam)
      if (category) params.set('category', category)
      if (tag) params.set('tag', tag)
      if (clientId) params.set('clientId', clientId)
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<ListResponse>(`/documents?${params.toString()}`)
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [searchParams, category, tag, clientId, page])

  useEffect(() => {
    void load()
  }, [load])

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page') next.delete('page')
    setSearchParams(next, { replace: true })
  }

  const onDownload = async (d: ExtendedDoc) => {
    try {
      const { url } = await api.get<{ url: string }>(
        `/documents/${d.id}/versions/${d.latestVersion}/download`
      )
      window.open(url, '_blank')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al descargar')
    }
  }

  const onSummarize = async (d: ExtendedDoc) => {
    setSummarizingId(d.id)
    try {
      const resp = await api.post<AiSummaryResponse>('/ai/summary', {
        contextType: 'DOCUMENT',
        contextId: d.id,
        style: 'BRIEF',
      })
      setAiResult({
        title: `Resumen — ${d.fileName}`,
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; code?: string }) : undefined
      if (body?.code === 'AI_NOT_CONFIGURED') {
        alert('Asistente IA no configurado en el servidor.')
      } else {
        alert(body?.error ?? (err instanceof Error ? err.message : 'Error'))
      }
    } finally {
      setSummarizingId(null)
    }
  }

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data]
  )

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <FileText size={20} className="text-toga-300" />
            Documentos
          </h1>
          {data && (
            <p className="text-xs t-muted">
              {data.total} documento{data.total !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <p className="text-xs t-faint">
          Para subir nuevos documentos, abre la ficha del expediente correspondiente.
        </p>
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nombre, notas, etiqueta, expediente o cliente…"
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
        <FilterSelect
          label="Cliente"
          value={clientId}
          onChange={(v) => setParam('clientId', v)}
          options={[
            { v: '', l: 'Todos' },
            ...clients.map((c) => ({ v: c.id, l: c.displayName })),
          ]}
        />
        <FilterSelect
          label="Categoría"
          value={category}
          onChange={(v) => setParam('category', v)}
          options={[
            { v: '', l: 'Todas' },
            ...Object.entries(DocumentCategoryLabel).map(([k, l]) => ({ v: k, l })),
          ]}
        />
        {tag && (
          <button
            type="button"
            onClick={() => setParam('tag', '')}
            className="px-2 py-1 rounded text-xs border border-toga-500/40 bg-toga-500/15 text-toga-200 inline-flex items-center gap-1.5"
            title="Quitar filtro de tag"
          >
            <TagIcon size={11} /> {tag} ✕
          </button>
        )}
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Tabla */}
      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/30 text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-3 py-2 text-left">Archivo</th>
              <th className="px-3 py-2 text-left">Expediente / Cliente</th>
              <th className="px-3 py-2 text-left">Categoría</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-right">Tamaño</th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> Cargando…
                </td>
              </tr>
            )}
            {!loading && data && data.items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center">
                  <FileText size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">Sin documentos que coincidan.</p>
                </td>
              </tr>
            )}
            {data?.items.map((d) => (
              <tr key={d.id} className="border-t border-base/40 hover:bg-hover">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-toga-300 shrink-0" />
                    <span className="t-primary font-medium truncate max-w-[280px]" title={d.fileName}>
                      {d.fileName}
                    </span>
                    {d.tags.length > 0 && (
                      <span className="flex items-center gap-1 flex-wrap">
                        {d.tags.slice(0, 3).map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setParam('tag', t)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300 hover:bg-slate-700/60 inline-flex items-center gap-1"
                          >
                            <TagIcon size={9} />
                            {t}
                          </button>
                        ))}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {d.caseId && d.caseCode ? (
                    <Link to={`/cases/${d.caseId}`} className="text-toga-300 hover:underline">
                      <span className="font-mono">{d.caseCode}</span>
                      {d.caseTitle && <span className="t-muted"> · {d.caseTitle}</span>}
                    </Link>
                  ) : d.clientDisplayName ? (
                    <span className="t-secondary">{d.clientDisplayName}</span>
                  ) : (
                    <span className="t-faint">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs t-secondary">
                  {DocumentCategoryLabel[d.category]}
                </td>
                <td className="px-3 py-2">
                  <DocIndexBadge status={d.indexStatus} />
                </td>
                <td className="px-3 py-2 text-right text-xs t-secondary font-mono">
                  {formatSize(d.latestSize)}
                </td>
                <td className="px-3 py-2 text-xs t-muted">
                  {new Date(d.createdAt).toLocaleDateString('es-ES')}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => void onSummarize(d)}
                      disabled={summarizingId !== null}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-300 disabled:opacity-40"
                      title="Resumir con IA"
                    >
                      {summarizingId === d.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Sparkles size={13} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDownload(d)}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-300"
                      title="Descargar"
                    >
                      <Download size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs t-muted">
          <span>
            Página {data.page} de {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={data.page <= 1}
              onClick={() => setParam('page', String(data.page - 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={data.page >= totalPages}
              onClick={() => setParam('page', String(data.page + 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {aiResult && (
        <AiResultModal
          title={aiResult.title}
          text={aiResult.text}
          conversationId={aiResult.conversationId}
          onClose={() => setAiResult(null)}
        />
      )}
    </div>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { v: string; l: string }[]
}) {
  return (
    <label className="text-xs t-muted flex items-center gap-2">
      <Filter size={12} className="t-faint" /> {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  )
}

